import React, { useState, useEffect } from 'react';
import { Button, Spinner, Container, Modal } from 'react-bootstrap';
import { FaTrash, FaUpload, FaImage, FaTimes, FaCheckSquare, FaSquare, FaExclamationTriangle } from 'react-icons/fa';
import Masonry from 'react-masonry-css';
import { toast } from 'react-toastify';
import SpPhotoUploadModal from '../../modals/watoaHudumaModal/SpPhotoUploadModal';
import { spPhotoApi } from '../../api/serviceProviders/spPhotoApi';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../../AdminPanel/picha/Picha.css';
import '../../AdminPanel/ecards/admin-ecards.css';

const Picha = () => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);

  // Single delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [photoToDelete, setPhotoToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Multi-select state
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);

  const fetchPhotos = async (currentPage = 1) => {
    if (currentPage === 1) setLoading(true);
    else setLoadingMore(true);

    try {
      const response = await spPhotoApi.getPhotos();
      const responseData = response?.data || {};
      const imagesArray = responseData?.data?.images || responseData?.images || responseData?.data || (Array.isArray(responseData) ? responseData : []);

      const pg = responseData?.total_pages ? {
        current_page: responseData.current_page || 1,
        total_pages: responseData.total_pages
      } : null;

      const newPhotos = Array.isArray(imagesArray) ? imagesArray : [];
      if (currentPage === 1) {
        setPhotos(newPhotos);
      } else {
        setPhotos(prev => {
          const existingIds = new Set(prev.map(i => i.id));
          return [...prev, ...newPhotos.filter(i => !existingIds.has(i.id))];
        });
      }
      setPagination(pg);
    } catch (err) {
      console.error('Error fetching photos:', err);
      toast.error('Imeshindwa kupata picha.');
      setPhotos([]);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    fetchPhotos(1);
  }, []);

  const loadMore = () => {
    if (pagination && page < pagination.total_pages) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchPhotos(nextPage);
    }
  };

  // ── Single delete ──────────────────────────────────────────
  const confirmDelete = (photo) => {
    if (!photo?.id) {
      toast.error('Haiwezekani kufuta picha hii — hakuna kitambulisho.');
      return;
    }
    setPhotoToDelete(photo);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!photoToDelete?.id) return;
    setDeleting(true);
    try {
      await spPhotoApi.deletePhoto(photoToDelete.id);
      toast.success('Picha imefutwa kikamilifu!');
      setShowDeleteModal(false);
      setPhotoToDelete(null);
      fetchPhotos(1);
    } catch (err) {
      console.error('Error deleting photo:', err);
      toast.error('Imeshindwa kufuta picha. Tafadhali jaribu tena.');
    } finally {
      setDeleting(false);
    }
  };

  // ── Multi-select delete ────────────────────────────────────
  const toggleSelectMode = () => {
    setSelectMode(prev => !prev);
    setSelectedIds([]);
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    setDeleting(true);
    try {
      await Promise.all(selectedIds.map(id => spPhotoApi.deletePhoto(id)));
      toast.success(`${selectedIds.length} picha zimefutwa kikamilifu!`);
      setShowBulkDeleteModal(false);
      setSelectedIds([]);
      setSelectMode(false);
      fetchPhotos(1);
    } catch (err) {
      console.error('Bulk delete error:', err);
      toast.error('Kuna tatizo kwenye kufuta baadhi ya picha.');
    } finally {
      setDeleting(false);
    }
  };

  const handleUploadSuccess = () => {
    setPage(1);
    fetchPhotos(1);
  };

  const breakpointColumnsObj = {
    default: 4,
    1100: 3,
    700: 2,
    500: 2
  };

  return (
    <Container className="pichaContainer bg-white mt-4" style={{ borderRadius: '15px' }}>
      <div className="pichaHeader text-center py-4">
        <h1 className="stylish-heading text-center" style={{ color: '#4169e1' }}>Picha na Kazi Zangu</h1>

        <div className="d-flex justify-content-center gap-3 mt-3 flex-wrap">
          <Button
            variant="primary"
            className="px-4 py-2 shadow"
            onClick={() => setShowModal(true)}
            style={{ borderRadius: '50px', background: 'linear-gradient(135deg, #4169e1 0%, #3a59c0 100%)', border: 'none' }}
          >
            <FaUpload className="me-2" /> Pakia Picha Mpya
          </Button>

          <Button
            variant={selectMode ? 'warning' : 'outline-secondary'}
            className="px-4 py-2"
            onClick={toggleSelectMode}
            style={{ borderRadius: '50px' }}
          >
            {selectMode ? <><FaTimes className="me-2" />Sitisha Uchaguzi</> : <><FaCheckSquare className="me-2" />Chagua Ili Kufuta</>}
          </Button>

          {selectMode && selectedIds.length > 0 && (
            <Button
              variant="danger"
              className="px-4 py-2 shadow"
              onClick={() => setShowBulkDeleteModal(true)}
              style={{ borderRadius: '50px' }}
            >
              <FaTrash className="me-2" /> Futa Zilizochaguliwa ({selectedIds.length})
            </Button>
          )}
        </div>

        {selectMode && (
          <p className="text-muted small mt-2 mb-0">
            Bonyeza kwenye picha kuzichagua, kisha ubonyeze <strong>Futa Zilizochaguliwa</strong>.
          </p>
        )}
      </div>

      {loading ? (
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
          <Spinner animation="border" variant="primary" />
        </div>
      ) : (
        <div className="mt-4 pb-4">
          {photos.length > 0 ? (
            <Masonry
              breakpointCols={breakpointColumnsObj}
              className="my-masonry-grid"
              columnClassName="my-masonry-grid_column"
            >
              {photos.map((photo) => {
                const photoSrc = photo.photo_path || photo.photo_url || photo.url || photo.lg_photo || photo.md_photo || photo;
                const isSelected = selectedIds.includes(photo.id);

                return (
                  <div
                    key={photo.id || Math.random()}
                    className="card-wrapper position-relative mb-4"
                    style={{
                      overflow: 'hidden',
                      borderRadius: '12px',
                      cursor: selectMode ? 'pointer' : 'default',
                      outline: isSelected ? '3px solid #dc3545' : 'none',
                      transition: 'outline 0.2s ease'
                    }}
                    onClick={() => selectMode && photo.id && toggleSelect(photo.id)}
                  >
                    <img
                      src={photoSrc}
                      className="card-img w-100 shadow-sm"
                      alt="Work Photo"
                      style={{ display: 'block', pointerEvents: selectMode ? 'none' : 'auto' }}
                    />

                    {/* Hover delete button when not in select mode */}
                    {!selectMode && (
                      <div
                        className="pichaOverlay position-absolute top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center"
                        style={{ background: 'rgba(0,0,0,0.5)', opacity: 0, transition: '0.3s' }}
                      >
                        <Button
                          variant="danger"
                          className="pichaDeleteButton"
                          style={{ borderRadius: '50%', width: '45px', height: '45px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            confirmDelete(photo);
                          }}
                        >
                          <FaTrash />
                        </Button>
                      </div>
                    )}

                    {/* Selection checkbox overlay when in select mode */}
                    {selectMode && (
                      <div
                        style={{
                          position: 'absolute',
                          top: '8px',
                          left: '8px',
                          color: isSelected ? '#dc3545' : '#fff',
                          fontSize: '22px',
                          filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.6))',
                          pointerEvents: 'none'
                        }}
                      >
                        {isSelected ? <FaCheckSquare /> : <FaSquare />}
                      </div>
                    )}
                  </div>
                );
              })}
            </Masonry>
          ) : (
            <div className="col-12 text-center py-5">
              <div className="pichaEmptyState bg-light rounded shadow-sm p-5 border border-dashed">
                <FaImage className="pichaEmptyIcon text-muted mb-3" size={48} />
                <h5 className="text-muted">Hujapakia picha zozote bado.</h5>
                <p className="text-muted small">Bonyeza kitufe hapo juu ili kuonyesha kazi zako kwa wateja!</p>
              </div>
            </div>
          )}

          {pagination && page < pagination.total_pages && (
            <div className="text-center mt-4 mb-2">
              <Button
                variant="primary"
                onClick={loadMore}
                disabled={loadingMore}
                style={{ padding: '10px 30px', borderRadius: '30px', background: 'linear-gradient(135deg, #4169e1 0%, #3a59c0 100%)', border: 'none' }}
              >
                {loadingMore ? <><Spinner size="sm" animation="border" className="me-2" />Inapakia...</> : 'Pakia Zaidi'}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Upload Modal */}
      <SpPhotoUploadModal
        show={showModal}
        handleClose={() => setShowModal(false)}
        onUploadSuccess={handleUploadSuccess}
      />

      {/* Single Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => !deleting && setShowDeleteModal(false)} centered>
        <Modal.Header closeButton={!deleting}>
          <Modal.Title className="text-danger">
            <FaTrash className="me-2" /> Futa Picha
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-4">
          {photoToDelete && (
            <img
              src={photoToDelete.photo_path || photoToDelete.photo_url || photoToDelete.url || photoToDelete.lg_photo}
              alt="To delete"
              style={{ width: '100%', maxHeight: '220px', objectFit: 'cover', borderRadius: '10px', marginBottom: '16px' }}
            />
          )}
          <p className="mb-0" style={{ fontSize: '1rem' }}>
            Una uhakika unataka <strong className="text-danger">kufuta kabisa</strong> picha hii? Hatua hii haiwezi kutenduliwa.
          </p>
        </Modal.Body>
        <Modal.Footer className="justify-content-center gap-3">
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)} disabled={deleting} style={{ borderRadius: '30px', minWidth: '110px' }}>
            Sitisha (Cancel)
          </Button>
          <Button variant="danger" onClick={handleDelete} disabled={deleting} style={{ borderRadius: '30px', minWidth: '130px' }}>
            {deleting ? <><Spinner size="sm" animation="border" className="me-2" />Inafuta...</> : <><FaTrash className="me-2" />Ndiyo, Futa</>}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Bulk Delete Confirmation Modal */}
      <Modal show={showBulkDeleteModal} onHide={() => !deleting && setShowBulkDeleteModal(false)} centered>
        <Modal.Header closeButton={!deleting}>
          <Modal.Title className="text-danger">
            <FaTrash className="me-2" /> Futa Picha {selectedIds.length}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-4">
          <div style={{ fontSize: '3rem', color: '#dc3545', marginBottom: '12px' }}>
            <FaTrash />
          </div>
          <p style={{ fontSize: '1rem' }}>
            Una uhakika unataka <strong className="text-danger">kufuta kabisa picha {selectedIds.length} zilizochaguliwa</strong>? Hatua hii haiwezi kutenduliwa.
          </p>
        </Modal.Body>
        <Modal.Footer className="justify-content-center gap-3">
          <Button variant="secondary" onClick={() => setShowBulkDeleteModal(false)} disabled={deleting} style={{ borderRadius: '30px', minWidth: '110px' }}>
            Sitisha (Cancel)
          </Button>
          <Button variant="danger" onClick={handleBulkDelete} disabled={deleting} style={{ borderRadius: '30px', minWidth: '160px' }}>
            {deleting ? <><Spinner size="sm" animation="border" className="me-2" />Inafuta Zote...</> : <><FaTrash className="me-2" />Futa Picha Zote</>}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default Picha;
